import React from "react";
import PropTypes from "prop-types";
import { List, Map } from "immutable";
import { Link } from "react-router-dom";
import { dollars } from "LIB/formatters/currency";
import { shortDate } from "LIB/formatters/date";
import {
  INVENTORIES,
  SERIAL_NUMBER,
  STORE_LOCATION_ID,
  STORE_OWNER_ID
} from "MODULES/inventories/constants";
import {
  EXPECTED_COMPONENT,
  WARRANTY_CONSIDERATION,
  APPROVED,
  getConditionLabel,
  LABOR_COST,
  OUTSIDE_LABOR_COST,
  PART_COST,
  SHOP_ASSIST_COST,
  CORE_RETURN_FEE_SAVINGS,
  DATE_RECEIVED
} from "MODULES/RepairOrders/constants";
import {
  ExternalLink,
  Icon,
  PoCode,
  PartNumber
} from "MODULES/shared/components";
import { getLocationNameById } from "MODULES/Locations/helpers";
import { CODE, ID } from "APP_ROOT/constants";
import { EXCHANGE_SALE, FLAT_EXCHANGE } from "MODULES/SalesOrders/constants";
import { addressFormat } from "LIB/formatters/address";
import { calculateSubtotal } from "MODULES/RepairOrders/helpers";
import { properCase } from "LIB/formatters/text";
import { OUTGOING_AWB_NUMBER } from "MODULES/Shipping/constants";
import { CAP_TYPES } from "../../../../PurchaseOrders/constants";

function InventorySection({ lineItem }) {
  const showEvalPeriod =
    lineItem.get("evaluation_period") && lineItem.get(DATE_RECEIVED);
  const scrapReport = lineItem.get("scrap_report");
  return (
    <div>
      <em className="text-muted">{lineItem.getIn(["part", "description"])}</em>
      <br />
      <br />
      <p>
        Serial #:{" "}
        <Link to={`/inventories/${lineItem.getIn(["inventory", "uuid"])}`}>
          {lineItem.get(SERIAL_NUMBER)}
        </Link>
      </p>
      <p>Quantity: {lineItem.get("quantity")}</p>
      <p>Condition Sent: {getConditionLabel(lineItem.get("condition_sent"))}</p>
      <p>
        As-is: {lineItem.getIn(["inventory", "as_is"], false) ? "Yes" : "No"}
      </p>
      {lineItem.get("cap_type") && (
        <p>
          {CAP_TYPES[lineItem.get("cap_type")].label} Cap:{" "}
          {dollars(lineItem.get("cap_amount"))}
        </p>
      )}
      {showEvalPeriod && (
        <p>
          Evaluation Period: {lineItem.get("evaluation_period")} days from{" "}
          {shortDate(lineItem.get("date_inventory_received"))}
        </p>
      )}
      <p>Store Owner: {getLocationNameById(lineItem.get(STORE_OWNER_ID))}</p>
      <p>
        Store Location: {getLocationNameById(lineItem.get(STORE_LOCATION_ID))}
      </p>
      {scrapReport && (
        <p>
          Scrap Report:{" "}
          <ExternalLink url={scrapReport}>Download Report</ExternalLink>
        </p>
      )}
      <p>
        Final Appr. On:{" "}
        {shortDate(lineItem.get("final_evaluation_approved_on"))}
      </p>
      {(lineItem.get("vendor_rma_status") || "") !== "" && (
        <p>
          Vendor RMA Status:
          {` ${properCase(lineItem.get("vendor_rma_status"))}`}
        </p>
      )}
    </div>
  );
}

InventorySection.propTypes = {
  lineItem: PropTypes.instanceOf(Map)
};

function OutgoingShipmentSection({ lineItem }) {
  return (
    <div>
      <p>Outgoing AWB #: {lineItem.get(OUTGOING_AWB_NUMBER)}</p>
      <p>Ship Date: {shortDate(lineItem.get("date_out"))}</p>
    </div>
  );
}

OutgoingShipmentSection.propTypes = {
  lineItem: PropTypes.instanceOf(Map)
};

function IncomingShipmentSection({ lineItem }) {
  return (
    <div>
      <p>Returning AWB #: {lineItem.get("incoming_awb_number")}</p>
      <p>Rec Date: {shortDate(lineItem.get("date_received"))}</p>
      <p>
        Condition Received:{" "}
        {lineItem.get("condition_received") &&
          INVENTORIES.condition[lineItem.get("condition_received")].label}
      </p>
    </div>
  );
}

IncomingShipmentSection.propTypes = {
  lineItem: PropTypes.instanceOf(Map)
};

function RecentOrdersSection({ lineItem }) {
  return (
    <div>
      {!lineItem.getIn(["inventory", "managed_repair_repaired"]) &&
        !lineItem.getIn(["inventory", "managed_repair_in_progress"]) && (
          <p>
            <span>PO #: </span>
            <PoCode inventory={lineItem.get("inventory", Map()).toJS()} />
          </p>
        )}
      {(lineItem.getIn(["inventory", "managed_repair_repaired"]) ||
        lineItem.getIn(["inventory", "managed_repair_in_progress"])) && (
        <p>
          <span>MR #:</span>
          <Link
            to={`/managed-repair-orders/${lineItem.getIn([
              "inventory",
              "managed_repair_details",
              "uuid"
            ])}`}
          >
            {lineItem.getIn(["inventory", "managed_repair_details", "code"])}
          </Link>
        </p>
      )}
      {lineItem.getIn(["inventory", "most_recent_sales_order", "id"]) && (
        <p>
          SO #:{" "}
          <Link
            to={`/sales-orders/${lineItem.getIn([
              "inventory",
              "most_recent_sales_order",
              "id"
            ])}`}
          >
            {lineItem.getIn(["inventory", "most_recent_sales_order", "code"])}
          </Link>
        </p>
      )}
    </div>
  );
}

RecentOrdersSection.propTypes = {
  lineItem: PropTypes.instanceOf(Map)
};

function ShopAssistSection({ lineItem }) {
  const shopAssistItems = lineItem
    .get("shop_assist_line_items", List())
    .sortBy(i => i.getIn(["repair_order", "code"]))
    .toJS();

  return (
    <div>
      {lineItem.get("shop_assist_repair_order_id") && [
        <br />,
        <p>
          Assists Repair Order:{" "}
          <Link
            to={`/repair-orders/${lineItem.get("shop_assist_repair_order_id")}`}
          >
            {lineItem.get("shop_assist_repair_order_code")}
          </Link>
        </p>
      ]}
      {shopAssistItems.length > 0 && [
        <br />,
        <p>
          Shop Assist Repair Orders:{" "}
          {shopAssistItems
            .map(i => (
              <Link to={`/repair-orders/${i.repair_order.id}`}>
                {i.repair_order.code}
              </Link>
            ))
            .reduce((prev, curr) => [prev, ", ", curr])}
        </p>
      ]}
    </div>
  );
}

ShopAssistSection.propTypes = {
  lineItem: PropTypes.instanceOf(Map)
};

function ReturnToSection({ lineItem }) {
  let shippingAddress = lineItem.get("return_to_address");
  const company = lineItem.get("return_to_company");

  if (company) {
    shippingAddress = shippingAddress.set("company_name", company.get("name"));
  }

  if (!shippingAddress) return null;

  return (
    <div>
      <h6 className="h4">Return To Address</h6>
      <table className="table table-condensed table-padding-horizonal-none margin-bottom-none">
        <tbody>
          <tr>
            <td>{shippingAddress && addressFormat(shippingAddress.toJS())}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

ReturnToSection.propTypes = {
  lineItem: PropTypes.instanceOf(Map)
};

function SalesOrderSection({ getField, lineItem }) {
  const salesOrder = lineItem.getIn(["inventory", "most_recent_sales_order"]);
  const totalCost = calculateSubtotal(getField)(
    [LABOR_COST, OUTSIDE_LABOR_COST, PART_COST],
    0
  );
  if (
    salesOrder &&
    !salesOrder.get(EXCHANGE_SALE) &&
    new Date(lineItem.get("created_at")) <
      new Date(
        lineItem.getIn(["inventory", "most_recent_sales_order", "created_at"])
      )
  ) {
    return (
      <div>
        <h6 className="h4">Sold At Shop</h6>
        <p>
          SO #:{" "}
          <Link to={`/sales-orders/${salesOrder.get(ID)}`}>
            {salesOrder.get(CODE)}
          </Link>
        </p>
      </div>
    );
  } else if (salesOrder && salesOrder.get(EXCHANGE_SALE)) {
    return (
      <div>
        <h6 className="h4">
          {salesOrder.get(FLAT_EXCHANGE) && "Flat "}Exchange SO
        </h6>
        <p>
          SO #:{" "}
          <Link to={`/sales-orders/${salesOrder.get(ID)}`}>
            {salesOrder.get(CODE)}
          </Link>
        </p>
        {totalCost > 0 && (
          <p>
            Core Return Fee Savings:{" "}
            {dollars(getField(CORE_RETURN_FEE_SAVINGS))}
          </p>
        )}
        {salesOrder.get("outright_value") && (
          <p>Outright Value: {dollars(salesOrder.get("outright_value"))}</p>
        )}
      </div>
    );
  }
  return null;
}

SalesOrderSection.propTypes = {
  getField: PropTypes.func.isRequired,
  lineItem: PropTypes.instanceOf(Map)
};

export default function Summary({ lineItem, getField, repairOrder }) {
  const cost = [LABOR_COST, OUTSIDE_LABOR_COST, PART_COST, SHOP_ASSIST_COST]
    .map(item => parseFloat(getField(item)) || 0)
    .reduce((sum, value) => sum + value, 0);
  const quantity = parseInt(lineItem.get("quantity") || 0, 10);
  const subtotal = cost * quantity;
  const withWarranty = getField("action_requested") === WARRANTY_CONSIDERATION;
  const finalWarrantyStatus = [
    getField("repair_station_warranty_status"),
    getField("warranty_status")
  ].every(f => [null, ""].includes(f))
    ? null
    : getField("repair_station_warranty_status") === APPROVED ||
      getField("warranty_status") === APPROVED;

  const WarrantyText = () => {
    if (!withWarranty || finalWarrantyStatus === null) return null;

    if (finalWarrantyStatus) {
      return (
        <span className="text-success">
          <Icon name="check" /> Approved
        </span>
      );
    }
    return (
      <span className="text-danger">
        <Icon name="close" /> Denied
      </span>
    );
  };

  return (
    <div className="well">
      <h6 className="h4">Part Summary</h6>
      <table className="table table-condensed table-padding-horizonal-none margin-bottom-none">
        <tbody>
          <tr>
            <td>
              Part #: <PartNumber part={lineItem.toJS().part} />
            </td>
          </tr>
          <tr>
            <td className="part-summary">
              <div>
                <InventorySection lineItem={lineItem} />
                <br />
                {withWarranty && (
                  <tr>
                    <td>
                      Final Warranty Status: <WarrantyText />
                    </td>
                  </tr>
                )}
                <br />
                {lineItem.get("action_requested") !== EXPECTED_COMPONENT && (
                  <div>
                    <OutgoingShipmentSection lineItem={lineItem} />
                    <br />
                  </div>
                )}
              </div>
              <IncomingShipmentSection lineItem={lineItem} />
              <br />
              {lineItem.get("action_requested") !== EXPECTED_COMPONENT && (
                <RecentOrdersSection lineItem={lineItem} />
              )}
              <ShopAssistSection lineItem={lineItem} />
            </td>
          </tr>
        </tbody>
      </table>
      <br />
      <SalesOrderSection lineItem={lineItem} getField={getField} />
      <ReturnToSection lineItem={lineItem} repairOrder={repairOrder} />
      <hr className="hr-xs" />
      <table className="table table-padding-horizonal-none">
        <tbody>
          <tr>
            <td>
              <h5 className="h4 margin-top-none margin-bottom-none">Total</h5>
            </td>
            <td className="text-right">
              <h5 className="h4 margin-top-none margin-bottom-none">
                {dollars(subtotal)}
              </h5>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

Summary.propTypes = {
  lineItem: PropTypes.shape({}),
  getField: PropTypes.func.isRequired,
  repairOrder: PropTypes.instanceOf(Map)
};



// WEBPACK FOOTER //
// ./app/modules/RepairOrders/components/Detail/LineItems/Summary.jsx